import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { toast } from 'sonner';
import {
  Camera,
  ZoomIn,
  ZoomOut,
  Crosshair,
  MapPin,
  Loader2,
  ChevronLeft,
  Move,
  Hand
} from 'lucide-react';

interface SatelliteViewCaptureProps {
  wellName: string;
  latitude: number;
  longitude: number;
  onCapture: (imageData: string, metadata: CaptureMetadata) => void;
  onCancel: () => void;
}

export interface CaptureMetadata {
  zoom: number;
  centerLat: number;
  centerLng: number;
  capturedAt: Date;
}

export default function SatelliteViewCapture({
  wellName,
  latitude,
  longitude,
  onCapture,
  onCancel
}: SatelliteViewCaptureProps) {
  const [zoom, setZoom] = useState(17);
  const [centerLat, setCenterLat] = useState(latitude);
  const [centerLng, setCenterLng] = useState(longitude);
  const [isLoading, setIsLoading] = useState(false);
  const [mapImageUrl, setMapImageUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);

  useEffect(() => {
    loadMapImage();
  }, [zoom, centerLat, centerLng]);

  const loadMapImage = async () => {
    setIsLoading(true);
    try {
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        throw new Error('Google Maps API key not configured');
      }

      // Larger image for better quality and dragging
      const size = '1200x900';
      const mapType = 'satellite';
      const center = `${centerLat},${centerLng}`;

      const url = `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${size}&maptype=${mapType}&key=${apiKey}`;

      setMapImageUrl(url);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading map:', error);
      toast.error('Failed to load satellite view');
      setIsLoading(false);
    }
  };

  const handleZoomChange = (value: number[]) => {
    setZoom(value[0]);
    setPosition({ x: 0, y: 0 });
    setDragOffset({ x: 0, y: 0 });
  };

  const handleRecenter = () => {
    setCenterLat(latitude);
    setCenterLng(longitude);
    setPosition({ x: 0, y: 0 });
    setDragOffset({ x: 0, y: 0 });
    toast.success('Recentered on well location');
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  // When drag stops, convert accumulated pixel offset to new center coordinates
  const handleDragStop = (_e: any, data: any) => {
    setIsDragging(false);

    // Calculate movement from ZERO (react-draggable resets position each drag)
    const deltaX = data.x;
    const deltaY = data.y;

    // Only update if there was actual movement
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      // Scale factor depends on zoom level - smaller movement at higher zoom
      const pixelToLatLng = 0.000005 * Math.pow(2, 18 - zoom);

      const latOffset = -deltaY * pixelToLatLng;
      const lngOffset = deltaX * pixelToLatLng;

      // Update center coordinates
      setCenterLat(prev => prev + latOffset);
      setCenterLng(prev => prev + lngOffset);
    }

    // RESET position to {x: 0, y: 0} for next drag
    // This prevents offset accumulation bug
    setPosition({ x: 0, y: 0 });
  };

  const handleCapture = async () => {
    if (!mapImageUrl) {
      toast.error('Map not loaded yet - please wait');
      return;
    }

    setIsLoading(true);

    try {
      // Fetch the image as blob
      const response = await fetch(mapImageUrl);
      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);

      const blob = await response.blob();

      // Convert to data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        const imageDataUrl = reader.result as string;

        if (!imageDataUrl || !imageDataUrl.startsWith('data:image')) {
          console.error('❌ Invalid image data');
          toast.error('Failed to process image');
          setIsLoading(false);
          return;
        }


        const metadata: CaptureMetadata = {
          zoom,
          centerLat,
          centerLng,
          capturedAt: new Date()
        };

        onCapture(imageDataUrl, metadata);
        setIsLoading(false);
      };

      reader.onerror = () => {
        console.error('❌ FileReader error');
        toast.error('Failed to process image');
        setIsLoading(false);
      };

      reader.readAsDataURL(blob);

    } catch (error) {
      console.error('❌ Capture error:', error);
      toast.error(`Failed: ${error instanceof Error ? error.message : 'Unknown'}`);
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4">
      <Card className="w-full max-w-5xl h-[95vh] sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-green-50 flex-shrink-0 py-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <MapPin className="h-5 w-5 text-blue-600" />
                Capture Satellite View
              </CardTitle>
              <p className="text-xs text-gray-600 mt-1 font-medium truncate max-w-[200px] sm:max-w-none">{wellName}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <ChevronLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Cancel</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-0 flex-1 overflow-y-auto relative">
          <div className="p-4 space-y-4">
            {/* Instructions - Collapsible on very small screens could be nice, but keeping simple */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <Move className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Controls</h3>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li>• Drag to move • Pinch/Slider to zoom • Center on red crosshair</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Map Display - Responsive Height */}
            <div
              className="relative bg-gray-900 rounded-lg overflow-hidden border-4 border-gray-300 mx-auto"
              style={{ height: '50vh', minHeight: '300px', maxHeight: '500px', width: '100%', touchAction: 'none' }}
            >
              {isLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 z-20">
                  <Loader2 className="h-10 w-10 animate-spin text-blue-500 mb-3" />
                  <p className="text-white text-xs">Loading...</p>
                </div>
              )}

              {mapImageUrl && !isLoading && (
                <Draggable
                  nodeRef={nodeRef}
                  position={position}
                  onStart={handleDragStart}
                  onStop={handleDragStop}
                  onDrag={(_e, data) => setPosition({ x: data.x, y: data.y })}
                  grid={[1, 1]}
                  scale={1}
                >
                  <div
                    ref={nodeRef}
                    className={`absolute ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                    style={{
                      width: '1200px',
                      height: '900px',
                      left: '50%',
                      top: '50%',
                      marginLeft: '-600px',
                      marginTop: '-450px'
                    }}
                  >
                    <img
                      src={mapImageUrl}
                      alt="Satellite View"
                      draggable={false}
                      className="w-full h-full select-none pointer-events-none"
                    />
                  </div>
                </Draggable>
              )}

              {/* Crosshair */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="relative">
                  <Crosshair className="h-12 w-12 text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.8)]" strokeWidth={2.5} />
                </div>
              </div>

              {/* Overlays */}
              <div className="absolute top-2 left-2 bg-black/70 text-white px-2 py-1 rounded text-xs z-20 pointer-events-none">
                {centerLat.toFixed(5)}, {centerLng.toFixed(5)}
              </div>

              {/* Drag Status */}
              {isDragging && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-green-500/90 text-white px-3 py-1 rounded-full text-xs font-bold z-20">
                  Dragging...
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="space-y-3 pb-2">
              <div className="flex items-center gap-2">
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setZoom(Math.max(15, zoom - 1))}
                  disabled={zoom <= 15}
                  className="h-9 w-9"
                >
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <div className="flex-1 px-2">
                  <Slider
                    value={[zoom]}
                    onValueChange={handleZoomChange}
                    min={15}
                    max={20}
                    step={1}
                  />
                </div>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setZoom(Math.min(20, zoom + 1))}
                  disabled={zoom >= 20}
                  className="h-9 w-9"
                >
                  <ZoomIn className="h-4 w-4" />
                </Button>
              </div>
              <div className="text-center text-xs text-gray-500">
                Zoom Level: {zoom}x
              </div>
            </div>
          </div>
        </CardContent>

        {/* Sticky Footer for Actions */}
        <div className="border-t p-3 bg-white flex-shrink-0 flex items-center justify-between gap-3">
          <Button variant="outline" onClick={handleRecenter} size="sm" className="hidden xs:flex">
            <Crosshair className="h-4 w-4 mr-2" />
            Reset Center
          </Button>
          <Button variant="outline" onClick={handleRecenter} size="icon" className="flex xs:hidden">
            <Crosshair className="h-4 w-4" />
          </Button>

          <div className="flex gap-2 flex-1 justify-end">
            <Button variant="ghost" onClick={onCancel} className="hidden sm:flex">
              Cancel
            </Button>
            <Button
              onClick={handleCapture}
              disabled={isLoading || !mapImageUrl}
              className="bg-gradient-to-r from-blue-600 to-green-600 flex-1 sm:flex-none"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Capture
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

